import React, { useRef, useState } from 'react';
import { Avatar, Button, Divider, Paper, Stack, Typography, Link as MuiLink } from '@mui/material';
import { AddOutlined, EditOutlined } from '@mui/icons-material';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete_icon.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/logout_icon.svg';
import { Card, Dialog, Ga4ButtonDefault, Ga4ButtonError, Ga4ButtonSuccess } from 'components';
import { useGa4, useUsers } from 'hooks';
import { getUserInitials } from 'utils';
import { RouteList } from 'types';
import { Link, useNavigate } from 'react-router-dom';
import { BUTTON_HEIGHT_CONSTANT, salutationOptions } from 'shared';

export const Profile = () => {
  const navigate = useNavigate();
  const { userProfileQuery } = useUsers();
  const { checkUserGa4ConnectionMutation, updateUserGa4PropertyIdMutation, isUserConnectionSuccess } = useGa4();
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const { data } = userProfileQuery;
  const userProfile = data?.user;

  const [isRemoveGa4DialogOpen, setIsRemoveGa4DialogOpen] = useState(false);

  const getSalutation = (salutation: string | null) => {
    if (!salutation || salutation === 'none') return '';
    return salutationOptions.find((option) => option.value === salutation)?.label ?? '';
  };

  const getOptionalString = (value: string | undefined) => (value ? `${value}, ` : '');

  const checkGa4PropertyId = () => {
    if (userProfile?.defaultGa4PropertyId) {
      checkUserGa4ConnectionMutation.mutate(userProfile.defaultGa4PropertyId);
    }
  };

  const navigateToEditPage = () => {
    anchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    navigate(RouteList.EditUserGa4);
  };

  return (
    <Paper
      ref={anchorRef}
      sx={({ spacing }) => ({
        p: 5,
        '& .MuiButtonBase-root': {
          fontSize: spacing(3),
          height: spacing(7),
          p: spacing(1),
          lineHeight: 1.2,
        },
        '& .MuiButton-startIcon': { marginRight: spacing(1), marginLeft: 0 },
        '& .MuiSvgIcon-root.MuiSvgIcon-fontSizeMedium': { fontSize: spacing(4) },
      })}
    >
      <Stack pb={5}>
        <Typography variant="h2">Mein Benutzerkonto</Typography>
      </Stack>
      <Divider />
      <Stack pt={5} gap={10}>
        <Stack gap={6} width={{ xs: 1, lg: 2 / 3 }}>
          <Typography variant="subtitle1">Allgemeine Informationen</Typography>
          <Card>
            <Stack direction="row" gap={6}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 20, fontWeight: 700 }}>
                {getUserInitials(userProfile)}
              </Avatar>
              <Stack flexGrow={1} justifyContent="center">
                <Typography variant="h3" lineHeight={1.4}>
                  {`${getSalutation(
                    userProfile?.defaultContactAddressSalutation ?? userProfile?.defaultBillingAddressSalutation ?? null
                  )} ${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`}
                </Typography>
                <Typography variant="body1">{userProfile?.companyName}</Typography>
              </Stack>
              <Stack justifyContent="center">
                <Link to={RouteList.Logout}>
                  <Button variant="outlined" startIcon={<LogoutIcon />} sx={{ pl: 3, pr: 2, py: 1, maxHeight: 30 }}>
                    LOGOUT
                  </Button>
                </Link>
              </Stack>
            </Stack>
          </Card>
        </Stack>

        <Stack gap={6} width={{ xs: 1, lg: 2 / 3 }}>
          <Typography variant="subtitle1">Zugangsdaten</Typography>
          <Card>
            <Stack gap={4}>
              <Typography variant="h4">E-Mail-Adresse</Typography>
              <Typography variant="body1">{userProfile?.email}</Typography>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between">
                <Stack gap={4}>
                  <Typography variant="h4">Passwort</Typography>
                  <Typography variant="body1">•••••••••••••••••</Typography>
                </Stack>
                <Link to={RouteList.ResetPassword}>
                  <Button
                    sx={(theme) => ({ maxHeight: theme.spacing(BUTTON_HEIGHT_CONSTANT) })}
                    startIcon={<EditOutlined />}
                  >
                    Passwort ändern
                  </Button>
                </Link>
              </Stack>
            </Stack>
          </Card>
        </Stack>
        <Stack gap={6} width={{ xs: 1, lg: 2 / 3 }}>
          <Typography variant="subtitle1">Adressen</Typography>
          <Card>
            <Stack direction="row" justifyContent="space-between">
              <Stack gap={4}>
                <Typography variant="h4">Firmenadresse</Typography>
                <Stack>
                  <Typography variant="body1">
                    {getSalutation(userProfile?.defaultContactAddressSalutation ?? null)}{' '}
                    {userProfile?.defaultContactAddressFirstName} {userProfile?.defaultContactAddressLastName}
                  </Typography>
                  <Typography variant="body1">{userProfile?.defaultContactAddressCompanyName}</Typography>
                  <Typography variant="body1">
                    {getOptionalString(userProfile?.defaultContactAddressLine1)}
                    {getOptionalString(userProfile?.defaultContactAddressLine2)}
                    {userProfile?.defaultContactAddressPostcode} {userProfile?.defaultContactAddressCity}
                  </Typography>
                </Stack>
                <Typography variant="body1">{userProfile?.defaultContactAddressPhone}</Typography>
              </Stack>
              <Link to={RouteList.EditContactAddress}>
                <Button startIcon={<EditOutlined />}>bearbeiten</Button>
              </Link>
            </Stack>
            <Divider sx={{ my: 6 }} />
            <Stack direction="row" justifyContent="space-between">
              <Stack gap={4}>
                <Typography variant="h4">Rechnungsadresse</Typography>
                <Stack>
                  <Typography variant="body1">
                    {getSalutation(userProfile?.defaultBillingAddressSalutation ?? null)}{' '}
                    {userProfile?.defaultBillingAddressFirstName} {userProfile?.defaultBillingAddressLastName}
                  </Typography>
                  <Typography variant="body1">{userProfile?.defaultBillingAddressCompanyName}</Typography>
                  <Typography variant="body1">
                    {getOptionalString(userProfile?.defaultBillingAddressLine1)}
                    {getOptionalString(userProfile?.defaultBillingAddressLine2)}
                    {userProfile?.defaultBillingAddressPostcode} {userProfile?.defaultBillingAddressCity}
                  </Typography>
                </Stack>
                <Typography variant="body1">{userProfile?.defaultBillingAddressPhone}</Typography>
              </Stack>
              <Link to={RouteList.EditBillingAddress}>
                <Button startIcon={<EditOutlined />}>bearbeiten</Button>
              </Link>
            </Stack>
          </Card>
        </Stack>
        <Stack gap={6} width={{ xs: 1, lg: 2 / 3 }}>
          <Typography variant="subtitle1">Verbindung zu Google Analytics 4</Typography>
          <Card>
            <Stack direction="row">
              <Stack gap={4} width={2 / 3} alignItems="start">
                <Typography variant="h4">Deine GA4 Property ID</Typography>
                <Stack gap={1} alignItems="start">
                  {userProfile?.defaultGa4PropertyId ? (
                    <>
                      <Typography>{userProfile.defaultGa4PropertyId}</Typography>

                      {isUserConnectionSuccess ? (
                        <Ga4ButtonSuccess />
                      ) : isUserConnectionSuccess === null ? (
                        <Ga4ButtonDefault onClick={checkGa4PropertyId} />
                      ) : (
                        <>
                          <Ga4ButtonError />
                          <Typography color="error.dark" sx={{ fontSize: 12, lineHeight: '150%' }}>
                            Ooops, etwas ist schiefgelaufen. Benötigst du Unterstützung? <br />
                            <MuiLink href={RouteList.Contact} color="error.dark">
                              Hier findest du weitere Hilfe
                            </MuiLink>
                            .
                          </Typography>
                        </>
                      )}
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<AddOutlined />}
                      sx={({ spacing }) => ({ px: spacing(2) })}
                      onClick={navigateToEditPage}
                    >
                      GA4 Property ID hinzufügen
                    </Button>
                  )}
                </Stack>
                <Typography>
                  Für einige Kampagnen musst du dein Google Analytics 4-Konto mit Alloy verknüpfen, damit Alloy das User
                  Engagement auswerten kann.
                </Typography>
              </Stack>
              {userProfile?.defaultGa4PropertyId && (
                <Stack width={1 / 3} alignItems="end" gap={2}>
                  <Button startIcon={<EditOutlined />} onClick={navigateToEditPage}>
                    Property id ändern
                  </Button>
                  <Button
                    startIcon={<DeleteIcon width={16} height={16} />}
                    onClick={() => setIsRemoveGa4DialogOpen(true)}
                  >
                    Verbindung trennen
                  </Button>
                </Stack>
              )}
            </Stack>
          </Card>
        </Stack>
      </Stack>
      <Dialog
        open={isRemoveGa4DialogOpen}
        title="Möchtest du die Verbindung wirklich trennen?"
        content={null}
        primaryActionTitle="Ja"
        secondaryActionTitle="Nein"
        onSecondaryActionClick={() => setIsRemoveGa4DialogOpen(false)}
        onPrimaryActionClick={() => {
          updateUserGa4PropertyIdMutation.mutateAsync('');
          setIsRemoveGa4DialogOpen(false);
        }}
      />
    </Paper>
  );
};
